import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useRoomStore } from '../stores/roomStore';
import { isRoomAdmin } from '../services/firebase/persistentRooms';
import { RoomSettingsHeader } from '../components/rooms/settings/RoomSettingsHeader';
import { RoomSettingsLoadingState } from '../components/rooms/settings/RoomSettingsLoadingState';
import { RoomInfoSection } from '../components/rooms/settings/RoomInfoSection';
import { RoomMembersSection } from '../components/rooms/settings/RoomMembersSection';
import { RoomActivityLogSection } from '../components/rooms/settings/RoomActivityLogSection';
import { RoomDangerZone } from '../components/rooms/settings/RoomDangerZone';

/**
 * Room settings page for admins to manage room and members.
 */
export default function RoomSettingsPage() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { currentRoom, loadRoom, clearCurrentRoom, isLoading } = useRoomStore();

  useEffect(() => {
    if (roomId) {
      loadRoom(roomId);
    }
    return () => clearCurrentRoom();
  }, [roomId, loadRoom, clearCurrentRoom]);

  useEffect(() => {
    if (currentRoom && user && !isRoomAdmin(currentRoom, user.uid, user.email)) {
      navigate(`/room/${roomId}`);
    }
  }, [currentRoom, user, roomId, navigate]);

  if (isLoading || !currentRoom) {
    return <RoomSettingsLoadingState />;
  }

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-bg-primary">
      <RoomSettingsHeader
        roomName={currentRoom.name}
        onBack={() => navigate('/')}
      />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <RoomInfoSection room={currentRoom} />
        <RoomMembersSection room={currentRoom} currentUserId={user.uid} />
        <RoomActivityLogSection roomId={currentRoom.id} />
        <RoomDangerZone room={currentRoom} />
      </main>
    </div>
  );
}
